import React, { useEffect, useState } from "react";
import { AppSidebar } from "../app-sidebar";
import { SidebarInset, SidebarProvider, SidebarTrigger } from "../ui/sidebar";
import { Separator } from "../ui/separator";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "../ui/breadcrumb";
import { NavUser } from "../nav-user";
import { Button } from "../ui/button";
import { Sparkles } from "lucide-react";
import { ChangelogDrawer } from "../changelog/ChangelogDrawer";
import { useChangelogSeen } from "../../hooks/useChangelogSeen";

import { useUIStore } from "../../store/uiStore";
import { useNavigateWithLock } from "../../hooks/useNavigateWithLock";
import BillingInfoCard from "../features/billing/BillingInfoCard";


interface AppLayoutProps {
  children: React.ReactNode;
  showSidebar?: boolean;
  showBillingInfoCard?: boolean;
  defaultSidebarOpen?: boolean;
  breadcrumbs?: Array<{
    title: string;
    href?: string;
    isCurrentPage?: boolean;
  }>;
}

export const AppLayout: React.FC<AppLayoutProps> = ({
  children,
  showSidebar = true,
  showBillingInfoCard = false,
  defaultSidebarOpen = true,
  breadcrumbs = [],
}) => {
  const { isSaving } = useUIStore();
  const navigate = useNavigateWithLock();
  const { hasNewChanges, markAsSeen } = useChangelogSeen();
  const [showSaved, setShowSaved] = useState(false);
  const [hasSavingStarted, setHasSavingStarted] = useState(false);
  const [isChangelogOpen, setIsChangelogOpen] = useState(false);

  const openChangelog = () => {
    markAsSeen();
    setIsChangelogOpen(true);
  };


  // Watch for when saving completes and show "Saved..." message
  useEffect(() => {
    if (!isSaving && showSaved) {
      // Hide the "Saved..." message after 2 seconds
      const timer = setTimeout(() => {
        setShowSaved(false);
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [isSaving, showSaved]);

  // Watch for when saving starts and ends
  useEffect(() => {
    if (isSaving) {
      setShowSaved(false);
      setHasSavingStarted(true);
    } else if (hasSavingStarted && !isSaving) {
      // This means saving just completed
      setShowSaved(true);
      setHasSavingStarted(false);
    }
  }, [isSaving, hasSavingStarted]);

  return (
    <SidebarProvider defaultOpen={defaultSidebarOpen}>
      {showSidebar && <AppSidebar />}
      <SidebarInset>
        <header className="flex h-16 shrink-0 items-center gap-2 transition-[width,height] ease-linear group-has-data-[collapsible=icon]/sidebar-wrapper:h-12 border-b border-sidebar-border">
          <div className="flex items-center gap-2 px-4 justify-between w-full">
            <div className="flex items-center gap-2">
              {showSidebar && <SidebarTrigger className="-ml-1" />}
              {showSidebar && (
                <Separator
                  orientation="vertical"
                  className="mr-2 data-[orientation=vertical]:h-4"
                />
              )}
              {breadcrumbs.length > 0 && (
                <Breadcrumb>
                  <BreadcrumbList>
                    {breadcrumbs.map((crumb, index) => (
                      <React.Fragment key={index}>
                        <BreadcrumbItem className="hidden md:block">
                          {crumb.isCurrentPage ? (
                            <BreadcrumbPage>
                              <b>{crumb.title}</b>
                            </BreadcrumbPage>
                          ) : (
                            <BreadcrumbLink
                              className="cursor-pointer"
                              onClick={() => {
                                if (crumb.href) {
                                  navigate(crumb.href as any);
                                }
                              }}
                            >
                              {crumb.title}
                            </BreadcrumbLink>
                          )}
                        </BreadcrumbItem>
                        {index < breadcrumbs.length - 1 && (
                          <BreadcrumbSeparator className="hidden md:block" />
                        )}
                      </React.Fragment>
                    ))}
                  </BreadcrumbList>
                </Breadcrumb>
              )}

              {(isSaving || showSaved) && (
                <div>
                  {isSaving ? (
                    <div className="flex items-center gap-2 text-amber-400">
                      <span className="text-xs font-medium">Saving...</span>{" "}
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-green-600">
                      <span className="text-xs font-medium">Saved</span>
                    </div>
                  )}
                </div>
              )}
            </div>
            {/* Billing Info Card */}
            {showBillingInfoCard && (
              <BillingInfoCard
                cardType="horizontal"
              />
            )}
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                className="relative h-8 w-8 p-0"
                onClick={openChangelog}
                title="Changelog"
              >
                <Sparkles className="h-4 w-4" />
                {hasNewChanges === true && (
                  <span
                    className="absolute animate-ping  duration-1000 -right-0.5 -top-0.5 h-2 w-2 rounded-full bg-red-500 ring-2 ring-background"
                    aria-hidden
                  />
                )}
              </Button>
              <NavUser />
            </div>
          </div>
        </header>
        <div className="flex flex-1 flex-col gap-4">{children}</div>
      </SidebarInset>
      <ChangelogDrawer
        isOpen={isChangelogOpen}
        onClose={() => setIsChangelogOpen(false)}
      />
    </SidebarProvider>
  );
};
